/* PledgeBankField: the "Pledge" tick next to a payment-terms box, and the bank
 * it names. One block for both looks and for every payment-terms box: the
 * offer, the solids recap legs, the fuels recap sale leg, and the outgoing
 * invoice and pro-forma headers. Credit and debit notes show it read-only.
 *
 * A pledge means a bank finances the deal and holds the goods as security, so
 * the customer must pay into that bank. The list is the banks our company
 * already holds accounts at (GET /api/invoicing/pledge-banks/?entity=). Once a
 * bank is chosen, the invoice bank box offers only that bank's accounts and
 * the server refuses any other (invoicing/bank_account_rules.py), so the
 * screen hiding things is a convenience, never the lock.
 *
 *   <PledgeBankField
 *     entity={companyIdOrName}          // the company the deal or document is for
 *     value={pledgeOf(fields, 'sale_')} // { is_pledged, pledge_bank_key, pledge_bank_name }
 *     onChange={(next) => setFields((p) => ({ ...p, ...pledgeFieldsFor(next, 'sale_') }))}
 *     locked lockedNote="..."           // kept by the deal: shown, not editable
 *     note="..."                        // e.g. the delivery prefill's explanation
 *     readOnly idPrefix="sale" />
 *
 * The tick is a plain checkbox outside <Field>: nested in Field, Field.css
 * would restyle it as a text box.
 *
 * IMT copy of HMS 930af5ee + ad335095 (classic look only): the bank box is
 * IMT's own Field with a native select, since IMT has no ui/fields Select.
 */
import React, { useEffect, useState } from 'react';
import invoicingService from '../../services/invoicingService';
import Field from '../ui/Field';
import './PledgeBankField.css';

const EMPTY = { is_pledged: false, pledge_bank_key: '', pledge_bank_name: '' };

/** A pledge out of a field set: the offer/invoice names, or a recap leg's (prefix 'sale_'). */
export const pledgeOf = (fields, prefix = '') => ({
  is_pledged: Boolean(fields?.[`${prefix}is_pledged`]),
  pledge_bank_key: fields?.[`${prefix}pledge_bank_key`] || '',
  pledge_bank_name: fields?.[`${prefix}pledge_bank_name`] || '',
});

/** The same pledge back under the field set's own names. */
export const pledgeFieldsFor = (pledge, prefix = '') => ({
  [`${prefix}is_pledged`]: Boolean(pledge.is_pledged),
  [`${prefix}pledge_bank_key`]: pledge.pledge_bank_key || '',
  [`${prefix}pledge_bank_name`]: pledge.pledge_bank_name || '',
});

/** An invoice-type document's pledge as the screens hold it (null -> the blank state). */
export const pledgeOfDocument = (data) => ({
  ...pledgeOf(data),
  pledge_from_deal: Boolean(data?.pledge_from_deal),
});

/** The note under a pledge the deal keeps: the prefill's own words, or the saved document's. */
export const dealPledgeNote = (doc) => (
  doc?.pledge_note || "This pledge comes from the deal's sales recap. Change it on the recap."
);

/**
 * The pledge part of an invoice or pro-forma save. On the first save of a
 * document raised from a delivery it also names the sales recaps the prefill
 * used, so the server reads the deal's pledge itself rather than trusting the
 * screen (``source_recap_ids``, never stored).
 */
export const pledgePayload = (doc, { isNew = false } = {}) => {
  const recapIds = doc?._selected_recap_ids;
  return {
    ...pledgeFieldsFor(pledgeOf(doc)),
    ...(isNew && Array.isArray(recapIds) && recapIds.length ? { source_recap_ids: recapIds } : {}),
  };
};

/** Load the pledge bank list for a company while `enabled`. */
export function usePledgeBanks(rawEntity, enabled) {
  const [state, setState] = useState({ banks: [], problem: null, loading: false });
  // Some screens hold the company as a picked option object, not its id.
  const entity = rawEntity && typeof rawEntity === 'object'
    ? (rawEntity.id ?? rawEntity.value ?? rawEntity.hms_entity_name ?? '')
    : rawEntity;

  useEffect(() => {
    if (!enabled) return undefined;
    if (entity === null || entity === undefined || entity === '') {
      setState({ banks: [], problem: 'Choose the IMT company first.', loading: false });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    invoicingService.fetchPledgeBanks(entity)
      .then((data) => {
        if (!cancelled) {
          setState({ banks: data?.banks || [], problem: data?.problem || null, loading: false });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        const refused = [401, 403].includes(error?.response?.status);
        setState({
          banks: [],
          loading: false,
          problem: refused
            ? (error.response.data?.detail || 'The pledge bank list is not available to your account.')
            : 'The pledge bank list could not be loaded. Try again, and tell IT if it keeps happening.',
        });
      });
    return () => { cancelled = true; };
  }, [entity, enabled]);

  return state;
}

export default function PledgeBankField({
  entity,
  value = EMPTY,
  onChange,
  locked = false,
  lockedNote = '',
  note = '',
  readOnly = false,
  idPrefix = 'pledge',
  className = '',
}) {
  const pledge = { ...EMPTY, ...value };
  const frozen = readOnly || locked;
  const { banks, problem, loading } = usePledgeBanks(entity, pledge.is_pledged && !frozen);

  const tickId = `${idPrefix}-pledge-tick`;
  const bankId = `${idPrefix}-pledge-bank`;

  const onTick = (event) => {
    if (!onChange) return;
    onChange(event.target.checked
      ? { ...pledge, is_pledged: true }
      : { ...EMPTY });
  };

  const onBank = (event) => {
    if (!onChange) return;
    const key = event.target.value;
    const bank = banks.find((b) => b.key === key);
    onChange({ is_pledged: true, pledge_bank_key: key, pledge_bank_name: bank ? bank.name : '' });
  };

  // A saved bank the list no longer holds (or a list still loading) still
  // shows, so the box never silently reads as "nothing chosen".
  const listed = banks.some((b) => b.key === pledge.pledge_bank_key);
  const savedOption = pledge.pledge_bank_key && !listed ? (
    <option value={pledge.pledge_bank_key}>
      {pledge.pledge_bank_name || pledge.pledge_bank_key}
      {!loading && !frozen && banks.length ? ' (no account on file any more)' : ''}
    </option>
  ) : null;

  let hint;
  if (pledge.is_pledged && !frozen) {
    hint = problem || 'Only accounts at this bank can be used on the invoices.';
  }

  return (
    <div className={`hms-pledge ${className}`.trim()}>
      <label className="hms-pledge-tick" htmlFor={tickId}>
        <input
          id={tickId}
          type="checkbox"
          className="hms-pledge-check"
          checked={pledge.is_pledged}
          onChange={onTick}
          disabled={frozen}
        />
        <span>Pledge</span>
      </label>

      {pledge.is_pledged && (
        <Field label="Pledge bank" required={!frozen} hint={hint}>
          <select
            id={bankId}
            aria-label="Pledge bank"
            required={!frozen}
            value={pledge.pledge_bank_key}
            onChange={onBank}
            disabled={frozen || loading}
          >
            <option value="">{loading ? 'Loading banks...' : 'Select the pledge bank...'}</option>
            {savedOption}
            {frozen ? null : banks.map((bank) => (
              <option key={bank.key} value={bank.key}>{bank.name}</option>
            ))}
          </select>
        </Field>
      )}

      {locked && lockedNote ? <p className="hms-pledge-note">{lockedNote}</p> : null}
      {note && note !== lockedNote ? <p className="hms-pledge-note">{note}</p> : null}
    </div>
  );
}
